import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Mail, CheckCircle, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NewsletterSignup = ({ variant = 'default' }) => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      toast.error('Bitte gib eine gültige E-Mail-Adresse ein.');
      return;
    }

    setIsLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/newsletter/subscribe`, {
        email: email.trim()
      });

      if (response.data.success) {
        setIsSuccess(true);
        toast.success(response.data.message);
        setEmail('');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Ein Fehler ist aufgetreten. Bitte versuche es später erneut.';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Success state
  if (isSuccess) {
    return (
      <div className={`flex items-center gap-3 ${variant === 'footer' ? 'text-gray-300' : 'text-gray-700'}`}>
        <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
        <span className="text-sm">
          Prüfe deinen Posteingang und bestätige die Anmeldung!
        </span>
      </div>
    );
  }

  // Footer variant - compact horizontal layout
  if (variant === 'footer') {
    return (
      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              type="email"
              placeholder="Deine E-Mail-Adresse"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="pl-10 bg-gray-800 border-gray-700 text-white placeholder:text-gray-500 focus:border-blue-500"
              disabled={isLoading}
              data-testid="newsletter-email-footer"
            />
          </div>
          <Button 
            type="submit" 
            disabled={isLoading}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4"
            data-testid="newsletter-submit-footer"
          >
            {isLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              'Anmelden'
            )}
          </Button>
        </div>
        <p className="text-xs text-gray-400">
          Mit der Anmeldung akzeptierst du unsere Datenschutzbestimmungen.
        </p>
      </form>
    );
  }

  // Default/Homepage variant - more prominent
  return (
    <div className="bg-gradient-to-r from-blue-50 to-teal-50 rounded-2xl p-6 md:p-8 border border-blue-100">
      <div className="max-w-xl mx-auto text-center">
        <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Mail className="w-6 h-6 text-blue-600" />
        </div>
        <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">
          Bleib auf dem Laufenden
        </h3>
        <p className="text-gray-600 mb-6">
          Erhalte aktuelle GmbH-Angebote, Tipps zur Gründung und exklusive Insights – direkt in dein Postfach.
        </p>
        
        <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
          <div className="relative flex-1">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              type="email"
              placeholder="Deine E-Mail-Adresse"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="pl-11 h-12 text-base border-gray-200 focus:border-blue-500"
              disabled={isLoading}
              data-testid="newsletter-email-home"
            />
          </div>
          <Button 
            type="submit" 
            size="lg"
            disabled={isLoading}
            className="h-12 px-6 bg-gradient-to-r from-blue-600 to-teal-600 hover:from-blue-700 hover:to-teal-700 text-white font-medium"
            data-testid="newsletter-submit-home"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Wird gesendet...
              </>
            ) : (
              'Jetzt anmelden'
            )}
          </Button>
        </form>
        
        <p className="text-xs text-gray-500 mt-4">
          Kein Spam. Du kannst dich jederzeit abmelden. <a href="/rechtliches#datenschutz" className="underline hover:text-blue-600">Datenschutz</a>
        </p>
      </div>
    </div>
  );
};

export default NewsletterSignup;
